/**
 * specialismMatch.js
 * ------------------
 * Pure helpers for the TD-27 specialism gate: which patients a clinician can see
 * and triage, derived from the clinician's specialism (asd / adhd / both) and the
 * patient's intake concern (asd / adhd / both, set by A3 / TD-72).
 *
 * The matching rule's single source of truth is
 * python-functions/constants/user_types.py (`specialism_can_access_concern`),
 * the authoritative server gate (rms_forms.py delegates to it via the thin
 * `_specialism_can_access_concern` wrapper). This client-side copy drives the
 * queue filter and read-only / disabled affordances so the two never disagree.
 */

import { CLINICIAN_SPECIALISM } from '../../constants/userTypes';

/** Intake-concern label carried by a patient record (set from the referral reason). */
export const INTAKE_CONCERN = { ASD: 'asd', ADHD: 'adhd', BOTH: 'both' };

export const INTAKE_CONCERN_LABELS = {
  [INTAKE_CONCERN.ASD]: 'ASD',
  [INTAKE_CONCERN.ADHD]: 'ADHD',
  [INTAKE_CONCERN.BOTH]: 'ASD & ADHD',
};

/** Human-readable label for an intake concern (empty string when unknown). */
export function getIntakeConcernLabel(concern) {
  const c = String(concern || '').trim().toLowerCase();
  return INTAKE_CONCERN_LABELS[c] || '';
}

/**
 * Whether a clinician's specialism covers a patient's intake concern.
 *
 *   - no specialism (admins/support, or not yet set) → no restriction (true)
 *   - 'both' specialism → covers everything
 *   - patient not yet classified (no intakeConcern) → not blocked
 *   - 'both' concern → visible to every specialism
 *   - otherwise the specialism must equal the concern
 *
 * @param {string|null|undefined} specialism  - clinician specialism claim
 * @param {string|null|undefined} intakeConcern - patient intake concern
 * @returns {boolean}
 */
export function canAccessConcern(specialism, intakeConcern) {
  const s = String(specialism || '').trim().toLowerCase();
  const c = String(intakeConcern || '').trim().toLowerCase();
  if (!s || s === CLINICIAN_SPECIALISM.BOTH) return true;
  if (!c || c === INTAKE_CONCERN.BOTH) return true;
  return s === c;
}

/**
 * Filter a list of patient records down to those a clinician with the given
 * specialism may access. A falsy specialism (admin/support, or unset) returns
 * the list unchanged.
 *
 * @param {Array<{intakeConcern?: string}>} patients
 * @param {string|null|undefined} specialism
 * @returns {Array}
 */
export function filterPatientsBySpecialism(patients, specialism) {
  const list = Array.isArray(patients) ? patients : [];
  const s = String(specialism || '').trim().toLowerCase();
  if (!s || s === CLINICIAN_SPECIALISM.BOTH) return list;
  return list.filter((p) => canAccessConcern(s, p?.intakeConcern));
}
